/**
 * Admin Email Templates Management Page
 * Manage automated email notification templates
 */

import React, { useState, useEffect } from 'react';
import {
  Mail,
  Search,
  Plus,
  Edit,
  Eye,
  RefreshCw,
  Send,
  Copy,
  CheckCircle,
  AlertCircle,
  FileText,
  Settings,
} from 'lucide-react';

interface EmailTemplate {
  id: number;
  name: string;
  slug: string;
  subject: string;
  description: string;
  category: 'billing' | 'support' | 'account' | 'marketing' | 'system';
  isActive: boolean;
  sentCount: number;
  lastSent?: string;
  lastModified: string;
}

const categoryConfig = {
  billing: { label: 'Billing', color: 'text-green-400', bgColor: 'bg-green-900/20' },
  support: { label: 'Support', color: 'text-orange-400', bgColor: 'bg-blue-900/20' },
  account: { label: 'Account', color: 'text-purple-400', bgColor: 'bg-purple-900/20' },
  marketing: { label: 'Marketing', color: 'text-orange-400', bgColor: 'bg-orange-900/20' },
  system: { label: 'System', color: 'text-slate-400', bgColor: 'bg-slate-900/20' },
};

const EmailTemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showPreview, setShowPreview] = useState<EmailTemplate | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/admin/email-templates', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setTemplates(data.data.templates);
      }
    } catch (error) {
      console.error('Failed to load email templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter((template) => {
    const searchMatch =
      searchQuery === '' ||
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase());

    const categoryMatch = filterCategory === 'all' || template.category === filterCategory;

    const statusMatch =
      filterStatus === 'all' ||
      (filterStatus === 'active' && template.isActive) ||
      (filterStatus === 'inactive' && !template.isActive);

    return searchMatch && categoryMatch && statusMatch;
  });

  const handleToggleActive = async (templateId: number, currentStatus: boolean) => {
    try {
      await fetch(`/api/v1/admin/email-templates/${templateId}/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ isActive: !currentStatus }),
      });
      loadTemplates();
    } catch (error) {
      console.error('Failed to toggle template status:', error);
      alert('Failed to update template status');
    }
  };

  const handleSendTest = async (templateId: number) => {
    const email = prompt('Enter email address to send test email:');
    if (!email) return;

    try {
      await fetch(`/api/v1/admin/email-templates/${templateId}/send-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ email }),
      });
      alert('Test email sent successfully!');
    } catch (error) {
      console.error('Failed to send test email:', error);
      alert('Failed to send test email');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return formatDate(dateString);
  };

  const activeTemplates = templates.filter((t) => t.isActive).length;
  const totalSent = templates.reduce((sum, t) => sum + t.sentCount, 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Mail className="w-8 h-8" />
            Email Templates
          </h1>
          <p className="text-slate-400 mt-2">
            Manage automated email notification templates
          </p>
        </div>
        <button
          onClick={() => setEditingTemplate({} as EmailTemplate)}
          className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2 font-medium"
        >
          <Plus className="w-5 h-5" />
          New Template
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Templates</p>
              <p className="text-2xl font-bold text-white mt-1">{templates.length}</p>
            </div>
            <FileText className="w-10 h-10 text-orange-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Active</p>
              <p className="text-2xl font-bold text-green-400 mt-1">{activeTemplates}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Sent</p>
              <p className="text-2xl font-bold text-purple-400 mt-1">
                {totalSent.toLocaleString()}
              </p>
            </div>
            <Send className="w-10 h-10 text-purple-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Categories</p>
              <p className="text-2xl font-bold text-orange-400 mt-1">
                {Object.keys(categoryConfig).length}
              </p>
            </div>
            <Settings className="w-10 h-10 text-orange-400" />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates by name, subject, or description..."
            className="w-full pl-10 pr-4 py-3 bg-slate-800/80 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
          />
        </div>

        <div className="flex gap-2">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-3 bg-slate-800/80 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
          >
            <option value="all">All Categories</option>
            {Object.entries(categoryConfig).map(([key, config]) => (
              <option key={key} value={key}>
                {config.label}
              </option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-3 bg-slate-800/80 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Templates List */}
      {loading ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50">
          <div className="flex items-center justify-center">
            <RefreshCw className="w-8 h-8 text-orange-400 animate-spin" />
            <span className="ml-3 text-slate-400">Loading templates...</span>
          </div>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50 text-center">
          <Mail className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No Templates Found</h3>
          <p className="text-slate-400">
            {searchQuery
              ? 'No templates match your search criteria.'
              : 'Create your first email template to get started.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredTemplates.map((template) => {
            const categoryInfo = categoryConfig[template.category];

            return (
              <div
                key={template.id}
                className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 hover:border-orange-500 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{template.name}</h3>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${categoryInfo.bgColor} ${categoryInfo.color}`}
                      >
                        {categoryInfo.label}
                      </span>
                      {template.isActive ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-900/20 text-green-400">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-900/20 text-slate-400">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Inactive
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-slate-400 mb-3">{template.description}</p>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500">Subject:</span>
                        <span className="text-slate-300">{template.subject}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500">Slug:</span>
                        <code className="px-2 py-0.5 bg-slate-900 text-orange-400 rounded font-mono text-xs">
                          {template.slug}
                        </code>
                      </div>
                    </div>

                    <div className="flex items-center gap-6 mt-4 pt-4 border-t border-slate-700/50 text-sm">
                      <div>
                        <span className="text-slate-500">Sent:</span>
                        <span className="text-white font-medium ml-2">
                          {template.sentCount.toLocaleString()}
                        </span>
                      </div>
                      {template.lastSent && (
                        <div>
                          <span className="text-slate-500">Last sent:</span>
                          <span className="text-slate-300 ml-2">
                            {formatRelativeTime(template.lastSent)}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className="text-slate-500">Modified:</span>
                        <span className="text-slate-300 ml-2">
                          {formatDate(template.lastModified)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 ml-6">
                    <button
                      onClick={() => setEditingTemplate(template)}
                      className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2 text-sm font-medium whitespace-nowrap"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => setShowPreview(template)}
                      className="px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 transition-colors flex items-center gap-2 text-sm font-medium whitespace-nowrap"
                    >
                      <Eye className="w-4 h-4" />
                      Preview
                    </button>
                    <button
                      onClick={() => handleSendTest(template.id)}
                      className="px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 transition-colors flex items-center gap-2 text-sm font-medium whitespace-nowrap"
                    >
                      <Send className="w-4 h-4" />
                      Test
                    </button>
                    <button
                      onClick={() => handleToggleActive(template.id, template.isActive)}
                      className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium whitespace-nowrap ${
                        template.isActive
                          ? 'bg-orange-900/20 text-orange-400 hover:bg-orange-900/30'
                          : 'bg-green-900/20 text-green-400 hover:bg-green-900/30'
                      }`}
                    >
                      {template.isActive ? (
                        <>
                          <AlertCircle className="w-4 h-4" />
                          Disable
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Enable
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white">Template Preview</h2>
              <button
                onClick={() => setShowPreview(null)}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Subject</label>
                <div className="px-4 py-2 bg-slate-900 border border-slate-700/50 rounded text-white">
                  {showPreview.subject}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">
                  Email Body Preview
                </label>
                <div className="px-4 py-4 bg-white text-gray-900 rounded min-h-[300px]">
                  <p className="text-slate-600">
                    Email template HTML preview would be rendered here...
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal Placeholder */}
      {editingTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-white mb-4">
              {editingTemplate.id ? 'Edit Template' : 'Create Template'}
            </h2>
            <p className="text-slate-400 mb-6">
              Template editor with rich text/HTML editor, variable insertion, and preview
              functionality will be implemented here.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setEditingTemplate(null)}
                className="px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 transition-colors"
              >
                Cancel
              </button>
              <button className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors">
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplatesPage;
